<template>
  <div class="page-max-width footer-max-width">
    <QHeader class="bg-white text-primary">
      <HeaderImageTitle :photo="etabProfile.getPhoto()">
        <QToolbar color="transparent">
          <QBtn v-if="!isAccountReadOnly || !isPasswordReadOnly" round color="white" icon="icon-f-x" dense flat @click="goToMainDashboard" />
          <QBtn v-if="isAccountReadOnly && isPasswordReadOnly" round color="white" icon="icon-f-arrow-left" dense flat @click="goToMainDashboard" />
          <QToolbarTitle>{{ etabProfile.getEtabName() }}</QToolbarTitle>
        </QToolbar>
      </HeaderImageTitle>
    </QHeader>

    <QPageContainer>
      <QPage padding>
        <AccountFormComponent v-if="!isAccountReadOnly && isPasswordReadOnly" :account="account" @on-save-quit="onSaveQuit" />
        <AccountReadOnlyComponent
          v-if="isAccountReadOnly && isPasswordReadOnly"
          :account="account"
          @pass-account-to-write-mode="passAccountToWriteMode"
        />
        <PasswordFormComponent v-if="!isPasswordReadOnly && isAccountReadOnly" @on-save-quit="onSaveQuit" />
        <PasswordReadOnlyComponent v-if="isPasswordReadOnly && isAccountReadOnly" @go-to-password-write-mode="goToPasswordWriteMode" />

        <div class="text-center q-mt-xl">
          <QBtn color="negative" :label="$t('ETAB.PAGES.PROFIL.@ACCOUNT_DELETION_BTN')" no-caps @click="onAccountDeletionRequest" />
        </div>
      </QPage>
    </QPageContainer>
  </div>
</template>

<script setup>
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { ScrollManager } from "@core/managers/scroll.manager";
  import { EtabAccountService } from "@core/services";
  import { ACTION_PROFILE_INIT, ACTION_ACCOUNT_INIT, GETTER_PROFILE_GET, GETTER_PROFILE_GET_ACCOUNT } from "@etab/stores/types"; // pinia
  import { useProfileStore } from "@etab/stores/profile";
  import { computed, onBeforeMount, ref } from "vue";
  import { useRouter } from "vue-router";
  import { useQuasar } from "quasar";
  import { useI18n } from "vue-i18n";

  import { ETAB_ROUTES } from "@etab/config";
  import { LoadingManager } from "@core/managers/loading.manager";
  import { AuthenticationManager } from "@core/managers/authentication.manager";

  import HeaderImageTitle from "@etab/components/dashboard/HeaderImageTitle.component.vue";
  import AccountReadOnlyComponent from "@etab/components/account/Account.readonly.component.vue";
  import AccountFormComponent from "@etab/components/account/Account.form.component.vue";
  import PasswordReadOnlyComponent from "@etab/components/account/Password.readonly.component.vue";
  import PasswordFormComponent from "@etab/components/account/Password.form.component.vue";

  const store = useProfileStore();
  const router = useRouter();
  const $q = useQuasar();
  const { t } = useI18n();

  const isPasswordReadOnly = ref(true);
  const isAccountReadOnly = ref(true);

  const etabProfile = computed(() => store[GETTER_PROFILE_GET]);
  const account = computed(() => store[GETTER_PROFILE_GET_ACCOUNT]);

  const accountInit = async () => {
    try {
      LoadingManager.show();
      await Promise.all([store[ACTION_PROFILE_INIT](), store[ACTION_ACCOUNT_INIT]()]);
    } catch (error) {
      NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
      throw error;
    } finally {
      LoadingManager.hide();
    }
  };

  const goToMainDashboard = () => {
    router.push({ name: ETAB_ROUTES.userMenu.root.name }).catch((suppressedError) => {});
  };

  const goToPasswordWriteMode = () => {
    isPasswordReadOnly.value = false;
    ScrollManager.scrollToTop();
  };

  const passAccountToWriteMode = () => {
    isAccountReadOnly.value = false;
  };

  const onSaveQuit = async ({ account, accountPassword }) => {
    try {
      LoadingManager.show();
      if (account) {
        await EtabAccountService.update({ account });
        NotificationsManager.success({ message: "SUCCESS.@SAVE_SUCCESS" });
        goToMainDashboard();
      }
      if (accountPassword) {
        await EtabAccountService.updatePassword({ accountPassword });
        NotificationsManager.success({ message: "SUCCESS.@SAVE_SUCCESS" });
        goToMainDashboard();
      }
    } catch (error) {
      if (accountPassword) {
        NotificationsManager.error({ message: "ERRORS.@WRONG_PASSWORD" });
      } else {
        NotificationsManager.error({ message: "ERRORS.@UNSAVED_MODIFICATIONS" });
      }
      throw error;
    } finally {
      LoadingManager.hide();
    }
  };

  const onAccountDeletionRequest = () => {
    $q.dialog({
      class: "extra-q-dialog-header-negative",
      title: t("ETAB.PAGES.PROFIL.@ACCOUNT_DELETION_MODAL_TITLE"),
      message: t("ETAB.PAGES.PROFIL.@ACCOUNT_DELETION_MODAL_MSG"),
      prompt: {
        model: "",
        label: t("ETAB.PAGES.PROFIL.@ACCOUNT_DELETION_MODAL_PROMPT"),
        isValid: (val) => val.toLocaleLowerCase() === "SUPPRIMER".toLocaleLowerCase(),
      },
      ok: {
        label: t("ETAB.PAGES.PROFIL.@ACCOUNT_DELETION_MODAL_CTA"),
        color: "negative",
        noCaps: true,
      },
      cancel: {
        label: t("ETAB.PAGES.PROFIL.@ACCOUNT_DELETION_MODAL_CANCEL"),
        flat: true,
        noCaps: true,
      },
      persistent: true,
    }).onOk(async (data) => {
      try {
        LoadingManager.show();
        await EtabAccountService.deleteAccount();
        await AuthenticationManager.logout();
        NotificationsManager.info({ message: "SUCCESS.@ACCOUNT_DELETION_SUCCESS" });
        router.push({ name: ETAB_ROUTES.auth.signup.name }).catch((suppressedError) => {});
      } catch (error) {
        NotificationsManager.contactEtabSupport();
        throw error;
      } finally {
        LoadingManager.hide();
      }
    });
  };

  onBeforeMount(async () => {
    await accountInit();
  });
</script>
